import api from './api';
import { GSTConfiguration, HSNMaster, Invoice, InvoiceLineItem, PaymentRecord, CreditNote } from '../types';

export const gstService = {
  // GST Configuration
  getGSTConfiguration: async () => {
    const response = await api.get('/api/gst-configurations?populate=*');
    return response.data;
  },

  updateGSTConfiguration: async (id: number, data: Partial<GSTConfiguration['attributes']>) => {
    const response = await api.put(`/api/gst-configurations/${id}`, { data });
    return response.data;
  },

  createGSTConfiguration: async (data: Partial<GSTConfiguration['attributes']>) => {
    const response = await api.post('/api/gst-configurations', { data });
    return response.data;
  },

  // HSN Master Management
  getHSNMasters: async (params?: Record<string, any>) => {
    const response = await api.get('/api/hsn-masters', { params });
    return response.data;
  },

  createHSNMaster: async (data: Partial<HSNMaster['attributes']>) => {
    const response = await api.post('/api/hsn-masters', { data });
    return response.data;
  },

  updateHSNMaster: async (id: number, data: Partial<HSNMaster['attributes']>) => {
    const response = await api.put(`/api/hsn-masters/${id}`, { data });
    return response.data;
  },

  deleteHSNMaster: async (id: number) => {
    const response = await api.delete(`/api/hsn-masters/${id}`);
    return response.data;
  },

  // HSN Master search
  searchHSNMasters: async (query: string) => {
    const response = await api.get(`/api/hsn-masters?filters[hsnCode][$containsi]=${query}&filters[isActive][$eq]=true`);
    return response.data;
  },

  // Invoice Management
  getInvoices: async (params?: Record<string, any>) => {
    const response = await api.get('/api/invoices?populate=*', { params });
    return response.data;
  },

  getInvoice: async (id: number) => {
    const response = await api.get(`/api/invoices/${id}?populate=*`);
    return response.data;
  },

  createInvoice: async (data: Partial<Invoice['attributes']>) => {
    const response = await api.post('/api/invoices', { data });
    return response.data;
  },

  updateInvoice: async (id: number, data: Partial<Invoice['attributes']>) => {
    const response = await api.put(`/api/invoices/${id}`, { data });
    return response.data;
  },

  deleteInvoice: async (id: number) => {
    const response = await api.delete(`/api/invoices/${id}`);
    return response.data;
  },

  // Create invoice from project
  createInvoiceFromProject: async (projectId: number, data: Partial<Invoice['attributes']>) => {
    const response = await api.post('/api/invoices/create-from-project', { 
      projectId,
      ...data 
    });
    return response.data;
  },

  // Invoice Line Items
  createInvoiceLineItem: async (data: Partial<InvoiceLineItem['attributes']>) => {
    const response = await api.post('/api/invoice-line-items', { data });
    return response.data;
  },

  updateInvoiceLineItem: async (id: number, data: Partial<InvoiceLineItem['attributes']>) => {
    const response = await api.put(`/api/invoice-line-items/${id}`, { data });
    return response.data;
  },

  deleteInvoiceLineItem: async (id: number) => {
    const response = await api.delete(`/api/invoice-line-items/${id}`);
    return response.data;
  },

  // Payment Records
  getPaymentRecords: async (params?: Record<string, any>) => {
    const response = await api.get('/api/payment-records?populate=*', { params });
    return response.data;
  },

  createPaymentRecord: async (data: Partial<PaymentRecord['attributes']>) => {
    const response = await api.post('/api/payment-records', { data });
    return response.data;
  },

  updatePaymentRecord: async (id: number, data: Partial<PaymentRecord['attributes']>) => {
    const response = await api.put(`/api/payment-records/${id}`, { data });
    return response.data;
  },

  deletePaymentRecord: async (id: number) => {
    const response = await api.delete(`/api/payment-records/${id}`);
    return response.data;
  },

  // Credit Notes
  getCreditNotes: async (params?: Record<string, any>) => {
    const response = await api.get('/api/credit-notes?populate=*', { params });
    return response.data;
  },

  getCreditNote: async (id: number) => {
    const response = await api.get(`/api/credit-notes/${id}?populate=*`);
    return response.data;
  },

  createCreditNote: async (data: Partial<CreditNote['attributes']>) => {
    const response = await api.post('/api/credit-notes', { data });
    return response.data;
  },

  updateCreditNote: async (id: number, data: Partial<CreditNote['attributes']>) => {
    const response = await api.put(`/api/credit-notes/${id}`, { data });
    return response.data;
  },

  approveCreditNote: async (id: number) => {
    const response = await api.post(`/api/credit-notes/${id}/approve`);
    return response.data;
  },

  // GST Reports
  getSalesRegisterReport: async (startDate: string, endDate: string) => {
    const response = await api.get(`/api/invoices/reports/sales-register`, {
      params: { startDate, endDate }
    });
    return response.data;
  },

  getGSTSummaryReport: async (period: string) => {
    const response = await api.get(`/api/invoices/reports/gst-summary`, {
      params: { period }
    });
    return response.data;
  },

  getOutstandingInvoicesReport: async () => {
    const response = await api.get('/api/invoices/reports/outstanding-invoices');
    return response.data;
  },

  getClientLedgerReport: async (clientId: number, startDate?: string, endDate?: string) => {
    const response = await api.get(`/api/invoices/reports/client-ledger/${clientId}`, {
      params: { startDate, endDate }
    });
    return response.data;
  },

  // Tax calculations
  calculateTaxes: async (lineItems: Partial<InvoiceLineItem['attributes']>[], clientStateCode: string, companyStateCode: string) => {
    const response = await api.post('/api/invoices/calculate-taxes', {
      lineItems,
      clientStateCode,
      companyStateCode
    });
    return response.data;
  },

  // Generate invoice PDF
  generateInvoicePDF: async (invoiceId: number) => {
    const response = await api.get(`/api/invoices/${invoiceId}/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Send invoice via email
  sendInvoiceEmail: async (invoiceId: number, emailData: { to: string; subject: string; message: string }) => {
    const response = await api.post(`/api/invoices/${invoiceId}/send-email`, emailData);
    return response.data;
  }
};

export default gstService; 